import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger,
  Field, Input, Persona, SearchBox, Select, Skeleton, SkeletonItem, Spinner, Tab, TabList,
} from "@fluentui/react-components";
import {
  AddRegular, ArrowClockwiseRegular, ArrowDownloadRegular, ChevronDownRegular, ChevronLeftRegular, ChevronRightRegular,
  ChevronUpRegular, DeleteRegular, DismissRegular, EditRegular, PanelLeftContractRegular, PanelLeftExpandRegular,
} from "@fluentui/react-icons";
import hz from "../HospitalityManagement.module.css";   // the existing styles are reused, never edited
import own from "./ModulesHub.module.css";
import { MODULE_CATALOG, ModDef, ModTable } from "./moduleCatalog";
import { ApiError, MetaColumn, TableMeta, cell, createRow, currentPropertyId, deleteRow, loadMeta, loadPage, patchRow } from "./modulesApi";

/* ------------------------------------------------------------------ helpers */
const allows = (ops: string, op: "Read" | "Create" | "Update" | "Delete") => ops === "All" || ops.split(",").map((s) => s.trim()).includes(op);
const NUMBER_TYPES = new Set(["Int16", "Int32", "Int64", "Byte", "SByte", "UInt16", "UInt32", "UInt64", "Decimal", "Double", "Single"]);
const nice = (name: string) => name.replace(/ID$/, " ID").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " ").trim();
const words = (v: string) => v.replace(/_/g, " ").toLowerCase().replace(/^\w/, (m) => m.toUpperCase());
const keyOf = (meta: TableMeta, row: Record<string, unknown>) => meta.key.map((k) => String(cell(row, k) ?? "")).join(",");

const GREEN = /^(ACTIVE|COMPLETED|PAID|CONFIRMED|APPROVED|OPEN|RESOLVED|CLOSED|DONE|AVAILABLE|READY|CLEAN|PASSED|COMPLIANT|VALID|SIGNED|CERTIFIED|SETTLED|POSTED|DELIVERED|RECEIVED|ARRIVED)$/;
const RED = /^(CANCELLED|CANCELED|NO_SHOW|FAILED|REJECTED|EXPIRED|OVERDUE|CRITICAL|BLOCKED|SUSPENDED|EXCLUDED|VOID|LOST|DECLINED|OUT_OF_ORDER|NON_COMPLIANT|BREACH)$/;
const AMBER = /^(PENDING|BOOKED|DRAFT|SCHEDULED|HOLD|WAITLIST|WAITING|PARTIAL|HIGH|MEDIUM|WARNING|REVIEW|PLANNED|REQUESTED|ISSUED|SUBMITTED)$/;
const BLUE = /^(IN_|CHECKED_IN|STARTED|RUNNING|ONGOING)/;
const tone = (v: string) => (RED.test(v) ? "red" : GREEN.test(v) ? "green" : AMBER.test(v) ? "amber" : BLUE.test(v) ? "blue" : "grey");
const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${hz.hzPill} ${hz["hzTone_" + tone(text)]}`}>{words(text)}</span>;

const fmt = (c: MetaColumn, v: unknown): string => {
  if (v === null || v === undefined || v === "") return "";
  if (c.type === "Boolean") return v ? "Yes" : "No";
  if (c.type === "DateTime" || c.type === "DateTimeOffset") { const d = new Date(String(v)); return isNaN(d.getTime()) ? String(v) : d.toLocaleString(); }
  if (c.type === "DateOnly") { const d = new Date(String(v) + "T00:00:00"); return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(); }
  if (c.type === "Decimal" || c.type === "Double" || c.type === "Single") return Number(v).toLocaleString("en-NG", { maximumFractionDigits: 4 });
  if (c.type === "Byte[]") return "(binary)";
  return String(v);
};
const toInput = (c: MetaColumn, v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (c.type === "DateTime" || c.type === "DateTimeOffset") return String(v).slice(0, 16);
  if (c.type === "DateOnly") return String(v).slice(0, 10);
  if (c.type === "TimeOnly") return String(v).slice(0, 5);
  return String(v);
};
const inputType = (c: MetaColumn) =>
  NUMBER_TYPES.has(c.type) ? "number" : c.type === "DateTime" || c.type === "DateTimeOffset" ? "datetime-local" : c.type === "DateOnly" ? "date" : c.type === "TimeOnly" ? "time" : "text";
const fromInput = (c: MetaColumn, s: string): unknown => {
  if (c.type === "Boolean") return s === "true";
  if (NUMBER_TYPES.has(c.type)) return Number(s);
  if (c.type === "TimeOnly" && s.length === 5) return `${s}:00`;
  if (c.type === "DateTime" && s.length === 16) return `${s}:00`;
  return s;
};
const isRight = (c: MetaColumn) => NUMBER_TYPES.has(c.type) && !c.key && !/ID$/.test(c.name);

const Tiles: React.FC<{ tiles: { label: string; value: string; note?: string }[] }> = ({ tiles }) => (
  <div className={hz.hzTiles}>
    {tiles.map((t) => (
      <div key={t.label} className={hz.hzTile}>
        <span className={hz.hzTileLabel}>{t.label}</span>
        <span className={hz.hzTileValue}>{t.value}</span>
        {t.note ? <span className={hz.hzTileNote}>{t.note}</span> : null}
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ one table */
interface TablePageProps { token?: string; table: ModTable; say: (m: string, err?: boolean) => void }
const PAGE_SIZE = 25;

const TablePage: React.FC<TablePageProps> = ({ token, table, say }) => {
  const [meta, setMeta] = useState<TableMeta | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string | undefined>(undefined);
  const [desc, setDesc] = useState(false);
  const [filterCol, setFilterCol] = useState("");
  const [filterVal, setFilterVal] = useState("");
  const [applied, setApplied] = useState<{ col: string; val: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selKey, setSelKey] = useState<string | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [tick, setTick] = useState(0);
  const reqId = useRef(0);
  const propertyId = currentPropertyId();

  useEffect(() => {
    let live = true;
    setMeta(null); setRows([]); setTotal(0); setPage(1); setSort(undefined); setDesc(false); setApplied(null);
    setFilterVal(""); setFilterCol(""); setError(null); setSelKey(null); setLoading(true);
    loadMeta(token, table.route).then((m) => { if (live) setMeta(m); })
      .catch((e) => { if (live) { setError(e instanceof ApiError ? e.message : "Could not load this screen."); setLoading(false); } });
    return () => { live = false; };
  }, [token, table.route]);

  const hasProp = useMemo(() => !!meta?.columns.some((c) => c.name === "PropertyID"), [meta]);

  useEffect(() => {
    if (!meta) return;
    const id = ++reqId.current;
    setLoading(true);
    const filters: Record<string, string> = {};
    if (hasProp && propertyId) filters["PropertyID"] = String(propertyId);
    if (applied && applied.val !== "") {
      const col = meta.columns.find((c) => c.name === applied.col);
      filters[col && col.type === "String" ? `${applied.col}_contains` : applied.col] = applied.val;
    }
    loadPage(token, table.route, { page, pageSize: PAGE_SIZE, sort, desc, filters })
      .then((r) => { if (id === reqId.current) { setRows(r.items ?? []); setTotal(r.total ?? 0); setError(null); } })
      .catch((e) => { if (id === reqId.current) { setRows([]); setTotal(0); setError(e instanceof ApiError ? e.message : "Could not load the records."); } })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [meta, token, table.route, page, sort, desc, applied, hasProp, propertyId, tick]);

  const cols = useMemo(() => (meta?.columns ?? []).filter((c) => c.type !== "Byte[]"), [meta]);
  const shown = useMemo(() => cols.slice(0, 9), [cols]);
  const canCreate = !!meta && allows(meta.operations, "Create");
  const canUpdate = !!meta && allows(meta.operations, "Update");
  const canDelete = !!meta && allows(meta.operations, "Delete");
  const sel = meta && selKey ? rows.find((r) => keyOf(meta, r) === selKey) ?? null : null;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const onSort = (name: string) => { if (sort === name) setDesc(!desc); else { setSort(name); setDesc(false); } setPage(1); };
  const apply = () => { setApplied(filterCol ? { col: filterCol, val: filterVal } : null); setPage(1); };

  const remove = async () => {
    if (!meta || !sel) return;
    try { await deleteRow(token, table.route, keyOf(meta, sel)); say("Deleted"); setSelKey(null); setTick((t) => t + 1); }
    catch (e) { say(e instanceof ApiError ? e.message : "Delete failed.", true); }
    setConfirmDel(false);
  };

  const exportCsv = () => {
    if (!meta) return;
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const lines = [cols.map((c) => esc(nice(c.name))).join(",")].concat(rows.map((r) => cols.map((c) => esc(fmt(c, cell(r, c.name)))).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${table.schema}-${table.table}.csv`.toLowerCase(); a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (c: MetaColumn, r: Record<string, unknown>) => {
    const v = cell(r, c.name);
    if (table.enums[c.name] && v) return <Pill text={String(v)} />;
    return fmt(c, v);
  };

  return (
    <>
      <Tiles tiles={[
        { label: "Records", value: meta ? total.toLocaleString("en-NG") : "…", note: applied ? "Filtered" : undefined },
        { label: "Page", value: meta ? `${page} of ${pages}` : "…" },
        ...(meta ? [{ label: "Columns", value: String(cols.length) }] : []),
      ]} />

      <div className={hz.hzCard}>
        <div className={hz.hzPane} role="toolbar" aria-label="Actions">
          {canCreate && <Button size="small" className={hz.hzPrimary} appearance="primary" icon={<AddRegular />} onClick={() => setForm("new")}>New</Button>}
          {(canUpdate || (meta && !canCreate)) && <Button size="small" appearance="subtle" icon={<EditRegular />} disabled={!sel} onClick={() => setForm("edit")}>{canUpdate ? "Edit" : "View"}</Button>}
          {canDelete && <Button size="small" appearance="subtle" icon={<DeleteRegular />} disabled={!sel} onClick={() => setConfirmDel(true)}>Delete</Button>}
          <span className={hz.hzGrow} />
          <Button size="small" appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={() => setTick((t) => t + 1)}>Refresh</Button>
          <Button size="small" appearance="subtle" icon={<ArrowDownloadRegular />} disabled={!rows.length} onClick={exportCsv}>Export</Button>
        </div>

        {meta && (
          <div className={hz.hzFilters}>
            <Select size="small" className={hz.hzSel} value={filterCol} onChange={(_, d) => setFilterCol(d.value)} aria-label="Filter column">
              <option value="">Filter by…</option>
              {cols.map((c) => <option key={c.name} value={c.name}>{nice(c.name)}</option>)}
            </Select>
            <Input size="small" className={hz.hzSearch} value={filterVal} placeholder="Value" onChange={(_, d) => setFilterVal(d.value)}
              onKeyDown={(e) => { if (e.key === "Enter") apply(); }} />
            <Button size="small" onClick={apply}>Apply</Button>
            {applied && <Button size="small" appearance="subtle" icon={<DismissRegular />} onClick={() => { setApplied(null); setFilterVal(""); setFilterCol(""); setPage(1); }}>Clear</Button>}
            {loading && <Spinner size="tiny" />}
          </div>
        )}

        {error && <div className={hz.hzErr} role="alert" style={{ margin: "0 12px 10px" }}>{error}</div>}

        <div className={hz.hzWork}>
          <div className={hz.hzGridWrap}>
            {!meta && loading && !error ? (
              <Skeleton aria-label="Loading" style={{ display: "grid", gap: 12, padding: "10px 0" }}>
                {[0, 1, 2, 3, 4, 5, 6].map((i) => <SkeletonItem key={i} size={16} />)}
              </Skeleton>
            ) : meta && (
              <table className={hz.hzGrid}>
                <thead>
                  <tr>
                    {shown.map((c) => (
                      <th key={c.name} className={isRight(c) ? hz.hzRight : undefined} aria-sort={sort === c.name ? (desc ? "descending" : "ascending") : "none"}>
                        <button type="button" className={hz.hzTh} onClick={() => onSort(c.name)}>
                          {nice(c.name)}
                          {sort === c.name && (desc ? <ChevronDownRegular fontSize={12} /> : <ChevronUpRegular fontSize={12} />)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const k = keyOf(meta, r);
                    return (
                      <tr key={k || i} className={k === selKey ? hz.hzRowSel : undefined} aria-selected={k === selKey} tabIndex={0}
                        onClick={() => setSelKey(k)} onDoubleClick={() => { setSelKey(k); setForm("edit"); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelKey(k); } }}>
                        {shown.map((c) => (
                          <td key={c.name} className={`${isRight(c) ? hz.hzRight : ""} ${c.key ? hz.hzMono : ""}`}>{renderCell(c, r)}</td>
                        ))}
                      </tr>
                    );
                  })}
                  {rows.length === 0 && !loading && <tr><td colSpan={shown.length} className={hz.hzEmpty}>Nothing here yet.{canCreate ? " Use New to add the first record." : ""}</td></tr>}
                </tbody>
              </table>
            )}
          </div>

          {sel && meta && (
            <aside className={hz.hzDetail} aria-label="Details">
              <div className={hz.hzDetailHead}>
                <strong>{table.label}</strong>
                <Button size="small" appearance="subtle" icon={<DismissRegular />} aria-label="Close details" onClick={() => setSelKey(null)} />
              </div>
              <dl className={hz.hzDl}>
                {cols.map((c) => (
                  <div key={c.name} className={hz.hzDlRow}>
                    <dt>{nice(c.name)}</dt>
                    <dd>{renderCell(c, sel) || "—"}</dd>
                  </div>
                ))}
              </dl>
            </aside>
          )}
        </div>

        <div className={hz.hzFoot}>
          <span>{meta ? `${rows.length} of ${total.toLocaleString("en-NG")} records` : ""}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Button size="small" appearance="subtle" icon={<ChevronLeftRegular />} disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page" />
            <span>Page {page} of {pages}</span>
            <Button size="small" appearance="subtle" icon={<ChevronRightRegular />} disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page" />
          </span>
        </div>
      </div>

      {form && meta && (
        <RecordDialog key={form + (form === "edit" && sel ? keyOf(meta, sel) : "")} token={token} table={table} meta={meta}
          mode={form} row={form === "edit" ? sel ?? undefined : undefined} readOnly={form === "edit" && !canUpdate} propertyId={propertyId}
          onClose={() => setForm(null)} onSaved={(m) => { say(m); setForm(null); setTick((t) => t + 1); }} />
      )}

      <Dialog open={confirmDel} onOpenChange={(_, d) => { if (!d.open) setConfirmDel(false); }}>
        <DialogSurface className={hz.hzDialogSm}>
          <DialogBody>
            <DialogTitle>Delete {table.label.toLowerCase()}?</DialogTitle>
            <DialogContent>This record will be removed. This can't be undone.</DialogContent>
            <DialogActions>
              <DialogTrigger action="close"><Button size="small">Cancel</Button></DialogTrigger>
              <Button size="small" className={hz.hzDanger} appearance="primary" onClick={() => void remove()}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

/* ------------------------------------------------------------------ create / edit dialog */
interface RecordDialogProps {
  token?: string; table: ModTable; meta: TableMeta; mode: "new" | "edit"; row?: Record<string, unknown>; readOnly: boolean;
  propertyId: number | null; onClose: () => void; onSaved: (msg: string) => void;
}
const RecordDialog: React.FC<RecordDialogProps> = ({ token, table, meta, mode, row, readOnly, propertyId, onClose, onSaved }) => {
  const compositeKey = meta.key.length > 1;
  const fields = useMemo(() => meta.columns.filter((c) => {
    if (c.type === "Byte[]" || c.concurrencyToken) return false;
    if (mode === "new") {
      if (c.readOnly) return false;
      if (c.key && !compositeKey) return false;                       // identity key: the database generates it
      if (c.name === "PropertyID" && propertyId) return false;        // filled from the property you are working in
    }
    return true;
  }), [meta, mode, compositeKey, propertyId]);

  const initial = useMemo(() => {
    const v: Record<string, string> = {};
    fields.forEach((c) => { v[c.name] = row ? toInput(c, cell(row, c.name)) : c.type === "Boolean" ? "false" : ""; });
    return v;
  }, [fields, row]);
  const [vals, setVals] = useState<Record<string, string>>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = (c: MetaColumn) => !readOnly && !(mode === "edit" && (c.key || c.readOnly));

  const save = async () => {
    const missing = fields.find((c) => editable(c) && c.type !== "Boolean" && !c.nullable && (vals[c.name] ?? "").trim() === "");
    if (missing) return setErr(`${nice(missing.name)} is required.`);
    const bad = fields.find((c) => editable(c) && NUMBER_TYPES.has(c.type) && vals[c.name] !== "" && Number.isNaN(Number(vals[c.name])));
    if (bad) return setErr(`${nice(bad.name)} must be a number.`);
    setBusy(true); setErr(null);
    try {
      if (mode === "new") {
        const body: Record<string, unknown> = {};
        fields.forEach((c) => { const s = (vals[c.name] ?? "").trim(); if (c.type === "Boolean") body[c.name] = s === "true"; else if (s !== "") body[c.name] = fromInput(c, s); });
        if (propertyId && meta.columns.some((c) => c.name === "PropertyID")) body["PropertyID"] = propertyId;
        await createRow(token, table.route, body);
        onSaved(`${table.label} created`);
      } else if (row) {
        const body: Record<string, unknown> = {};
        fields.forEach((c) => {
          if (!editable(c) || vals[c.name] === initial[c.name]) return;
          body[c.name] = vals[c.name].trim() === "" ? null : fromInput(c, vals[c.name].trim());
        });
        if (Object.keys(body).length === 0) return onClose();
        const cc = meta.columns.find((c) => c.concurrencyToken);
        if (cc) body[cc.name] = cell(row, cc.name);                                 // optimistic concurrency
        await patchRow(token, table.route, keyOf(meta, row), body);
        onSaved(`${table.label} saved`);
      }
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface className={hz.hzDialog}>
        <DialogBody>
          <DialogTitle action={<DialogTrigger action="close"><Button size="small" appearance="subtle" aria-label="Close" icon={<DismissRegular />} /></DialogTrigger>}>
            {mode === "new" ? `New ${table.label.toLowerCase()}` : readOnly ? table.label : `Edit ${table.label.toLowerCase()}`}
          </DialogTitle>
          <DialogContent>
            <div className={hz.hzForm} style={{ maxHeight: "60vh", overflow: "auto" }}>
              {fields.map((c) => {
                const opts = table.enums[c.name];
                const long = c.type === "String" && /(Notes?|Description|Details|Reason|Comment|Address|Terms|Text|Body|Summary)$/i.test(c.name);
                return (
                  <div key={c.name} className={long ? hz.hzFormFull : undefined}>
                    {c.type === "Boolean" ? (
                      <Checkbox label={nice(c.name)} checked={vals[c.name] === "true"} disabled={!editable(c)} onChange={(_, d) => setVals({ ...vals, [c.name]: d.checked === true ? "true" : "false" })} />
                    ) : (
                      <Field label={nice(c.name)} required={!c.nullable && editable(c)}>
                        {opts ? (
                          <Select value={vals[c.name] ?? ""} disabled={!editable(c)} onChange={(_, d) => setVals({ ...vals, [c.name]: d.value })}>
                            <option value="">{c.nullable ? "—" : "Select…"}</option>
                            {opts.map((o) => <option key={o} value={o}>{words(o)}</option>)}
                          </Select>
                        ) : (
                          <Input type={inputType(c)} value={vals[c.name] ?? ""} disabled={!editable(c)} onChange={(_, d) => setVals({ ...vals, [c.name]: d.value })} />
                        )}
                      </Field>
                    )}
                  </div>
                );
              })}
            </div>
            {err && <div className={hz.hzErr} role="alert">{err}</div>}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close"><Button size="small">{readOnly ? "Close" : "Cancel"}</Button></DialogTrigger>
            {!readOnly && <Button size="small" className={hz.hzPrimary} appearance="primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : mode === "new" ? "Create" : "Save"}</Button>}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ the page */
export interface ModulesHubProps {
  /** central-auth bearer token (the same one the other HMS screens use) */
  token?: string;
  /** whether the module sidebar is open (it slides shut inside the page) */
  drawerOpen: boolean;
  onDrawerOpenChange: (open: boolean) => void;
}
let remembered: { mod: string | null; table: string | null } = { mod: null, table: null };   // keeps your place when you switch tabs

const GROUPS = (() => {
  const g: [string, ModDef[]][] = [];
  MODULE_CATALOG.forEach((m) => { const e = g.find((x) => x[0] === m.group); if (e) e[1].push(m); else g.push([m.group, [m]]); });
  return g;
})();
const TOTAL_SCREENS = MODULE_CATALOG.reduce((n, m) => n + m.tables.length, 0);
const tk = (t: ModTable) => `${t.schema}.${t.table}`;

const ModulesHub: React.FC<ModulesHubProps> = ({ token, drawerOpen, onDrawerOpenChange }) => {
  const [modKey, setModKeyRaw] = useState<string | null>(remembered.mod);
  const [tableKey, setTableKeyRaw] = useState<string | null>(remembered.table);
  const [q, setQ] = useState("");
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const setNav = (m: string | null, t: string | null) => { remembered = { mod: m, table: t }; setModKeyRaw(m); setTableKeyRaw(t); };
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);
  const say = useCallback((msg: string, err = false) => setToast({ msg, err }), []);

  const mod = MODULE_CATALOG.find((m) => m.key === modKey) ?? null;
  const table = mod?.tables.find((t) => tk(t) === tableKey) ?? null;
  const needle = q.trim().toLowerCase();
  const visible = useMemo(() => GROUPS.map(([g, list]) => [g, list.map((m) => {
    if (!needle) return { m, tables: m.tables, open: m.key === modKey };
    const titleHit = m.title.toLowerCase().includes(needle);
    const tables = titleHit ? m.tables : m.tables.filter((t) => t.label.toLowerCase().includes(needle));
    return tables.length || titleHit ? { m, tables, open: true } : null;
  }).filter(Boolean) as { m: ModDef; tables: ModTable[]; open: boolean }[]] as const).filter(([, l]) => l.length > 0), [needle, modKey]);

  const closeIfNarrow = () => { if (window.matchMedia("(max-width: 1000px)").matches) onDrawerOpenChange(false); };
  const open = (m: ModDef, t?: ModTable) => { setNav(m.key, t ? tk(t) : null); closeIfNarrow(); };

  return (
    <div className={`${hz.hzShell} ${own.shell}`}>
      <aside className={`${hz.WRe37} ${own.side} ${drawerOpen ? "" : own.sideClosed}`} aria-label="Modules" aria-hidden={!drawerOpen}>
        <div className={own.sideInner}>
          <div className={hz.HDs1}>
            <span className={hz.HEwy}>Modules</span>
            <Button shape="circular" size="small" appearance="subtle" aria-label="Hide modules" icon={<PanelLeftContractRegular />} onClick={() => onDrawerOpenChange(false)} />
          </div>
          <div className={hz.SRCcont}>
            <SearchBox className={hz.Fiwb} placeholder="Search modules and screens" appearance="filled-darker" value={q} onChange={(_, d) => setQ(d.value)} />
          </div>
          <div className={`${own.sideList} ${hz.customScrollBar} ${hz.LOWqs}`}>
            {visible.length === 0 && <p className={own.noMatch}>Nothing matches “{q}”.</p>}
            {visible.map(([group, list]) => (
              <div key={group}>
                <div className={`${hz.HJne5} ${own.groupHead}`}>
                  <div className={hz.Y5rV}>{group}</div>
                  <div className={own.count} style={{ marginLeft: "auto" }}>{list.length}</div>
                </div>
                <TabList vertical selectedValue={tableKey ? `${modKey}|${tableKey}` : modKey ? `m|${modKey}` : ""}>
                  {list.map(({ m, tables, open: isOpen }) => (
                    <div key={m.key}>
                      <Tab value={`m|${m.key}`} className={`${hz.Mvtuisvt} ${modKey === m.key ? hz.cgTactive : ""}`} onClick={() => open(m)}>
                        <div className={hz.Cgscf}>
                          <Persona name={m.title} secondaryText={`${m.tables.length} screens`} avatar={{ color: "colorful" }} />
                          {isOpen ? <ChevronUpRegular fontSize={12} /> : <ChevronDownRegular fontSize={12} />}
                        </div>
                      </Tab>
                      {isOpen && (
                        <div className={own.screens}>
                          {tables.map((t) => (
                            <button key={tk(t)} type="button" className={`${own.screenItem} ${modKey === m.key && tableKey === tk(t) ? own.screenOn : ""}`} onClick={() => open(m, t)}>{t.label}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </TabList>
              </div>
            ))}
          </div>
        </div>
      </aside>

      <div className={hz.hzHost}>
        <div className={hz.hzPg}>
          <header className={hz.hzHead}>
            <div className={own.headRow}>
              {!drawerOpen && <Button size="small" appearance="subtle" aria-label="Show modules" icon={<PanelLeftExpandRegular />} onClick={() => onDrawerOpenChange(true)} />}
              <div className={own.headText}>
                <h2 className={hz.hzTitle}>{table ? table.label : mod ? mod.title : "More modules"}</h2>
                <div className={own.crumbs}>
                  {!mod && <span>{MODULE_CATALOG.length} modules · {TOTAL_SCREENS} screens, all saved to the live database.</span>}
                  {mod && <button type="button" onClick={() => setNav(null, null)}>Modules</button>}
                  {mod && <span>›</span>}
                  {mod && (table ? <button type="button" onClick={() => setNav(mod.key, null)}>{mod.title}</button> : <span>{mod.tables.length} screens</span>)}
                  {table && <><span>›</span><span>{table.schema}.{table.table}</span></>}
                </div>
              </div>
            </div>
          </header>

          {!mod && (
            <>
              <Tiles tiles={[{ label: "Modules", value: String(MODULE_CATALOG.length) }, { label: "Screens", value: String(TOTAL_SCREENS) }, { label: "Groups", value: String(GROUPS.length) }]} />
              {GROUPS.map(([g, list]) => (
                <React.Fragment key={g}>
                  <div className={own.groupTitle}>{g}</div>
                  <div className={own.cards}>
                    {list.map((m) => (
                      <button key={m.key} type="button" className={own.cardBtn} onClick={() => open(m)}>
                        <div className={hz.hzPanel}>
                          <h3 className={hz.hzPanelTitle}>{m.title}</h3>
                          <p className={hz.hzPanelNote} style={{ marginBottom: 0 }}>{m.tables.length} screens</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </>
          )}

          {mod && !table && (
            <>
              <Tiles tiles={[{ label: "Screens", value: String(mod.tables.length) }]} />
              <div className={own.cards}>
                {mod.tables.map((t) => (
                  <button key={tk(t)} type="button" className={own.cardBtn} onClick={() => open(mod, t)}>
                    <div className={hz.hzPanel}>
                      <h3 className={hz.hzPanelTitle}>{t.label}</h3>
                      <p className={hz.hzPanelNote} style={{ marginBottom: 0 }}>{t.schema}.{t.table}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {mod && table && <TablePage key={table.route} token={token} table={table} say={say} />}
        </div>
      </div>

      {toast && <div className={`${hz.hzToast} ${toast.err ? hz.hzToastErr : ""}`} role="status" aria-live="polite">{toast.msg}</div>}
    </div>
  );
};

export default ModulesHub;
